import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Layers3, Search, X } from 'lucide-react'
import type { PrintBookModuleOption } from './printBookBulkManual'

interface BookBulkModulePickerProps {
  options: PrintBookModuleOption[]
  selectedIds: number[]
  onApply: (moduleIds: number[]) => void
  onClose: () => void
}

export function BookBulkModulePicker({
  options,
  selectedIds,
  onApply,
  onClose,
}: BookBulkModulePickerProps) {
  const [keyword, setKeyword] = useState('')
  const [draftIds, setDraftIds] = useState(() => new Set(selectedIds))

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const filteredOptions = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    if (!normalizedKeyword) return options

    return options.filter((option) => {
      const searchTarget = `${option.title} ${option.id} ${option.parentPath.join(' ')}`
      return searchTarget.toLocaleLowerCase().includes(normalizedKeyword)
    })
  }, [keyword, options])

  const toggleModule = (moduleId: number) => {
    setDraftIds((current) => {
      const next = new Set(current)
      if (next.has(moduleId)) next.delete(moduleId)
      else next.add(moduleId)
      return next
    })
  }

  const handleApply = () => {
    const orderedIds = options.filter((option) => draftIds.has(option.id)).map((option) => option.id)
    onApply(orderedIds)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-module-picker-title"
        className="flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h2 id="bulk-module-picker-title" className="text-lg font-bold text-slate-900">교재에 담을 모듈 선택</h2>
            <p className="mt-1 text-sm text-slate-500">선택한 노출 범위의 하위 모듈만 표시됩니다.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="모듈 선택 닫기"
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="모듈명, 상위 코스 또는 ID 검색"
              className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-3">
          {filteredOptions.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredOptions.map((option) => {
                const selected = draftIds.has(option.id)
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    onClick={() => toggleModule(option.id)}
                    className="group flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left transition-colors hover:bg-amber-50/70"
                  >
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
                      selected
                        ? 'border-amber-500 bg-amber-500 text-slate-950'
                        : 'border-slate-300 bg-white text-transparent group-hover:border-amber-400'
                    }`}>
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">{option.title}</span>
                      <span className="mt-0.5 block truncate text-xs text-slate-400">
                        {option.parentPath.join(' › ')} · Module #{option.id}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Layers3 className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-600">조건에 맞는 모듈이 없습니다</p>
              <p className="mt-1 text-xs text-slate-400">검색어를 변경하거나 다른 범위 콘텐츠를 선택해 보세요.</p>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={() => setDraftIds(new Set())}
            disabled={draftIds.size === 0}
            className="text-sm font-medium text-slate-500 transition hover:text-slate-800 disabled:opacity-40"
          >
            선택 초기화
          </button>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-500">{draftIds.size}개 선택</span>
            <button
              type="button"
              onClick={handleApply}
              className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
            >
              선택 완료
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
